import {
  useCallback, useMemo,
} from 'react';
import { useTranslation } from '~/_shared/utils/hooks';
import { notNull } from '~/_shared/utils/typeGuards';
import { useBoundaryTerritoryGroupDisplayName } from '~/sidebar/sidebarApps/mapTools/boundary/hooks/useBoundaryTerritoryGroupDisplayName';
import type {
  BoundaryGroupId, BoundaryTerritoryGroupId,
} from '~/store/boundaries/boundaryIdentifier.type';
import { useBoundaryGroupsSelector } from '~/store/boundaryGroups/boundaryGroups.selectors';
import { useBoundaryTerritoryGroupsSelector } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.selectors';
import {
  createChartBoundaryStatistics, createChartId,
  createChartMetricDemographic,
  createChartMetricLocationCount,
  createChartMetricSpreadsheetColumn,
} from '~/store/mapSettings/data/charts/mapSettingsCharts.factory';
import {
  type ChartBoundaryStatistics, type ChartId,
  ChartMetricType,
} from '~/store/mapSettings/data/charts/mapSettingsCharts.types';

// Global map for stable chart IDs - persists across renders and component instances
const defaultStatisticsChartIds = new Map<BoundaryTerritoryGroupId, ChartId>();

const getOrCreateBoundaryDefaultStatsChartId = (boundaryTerritoryGroupId: BoundaryTerritoryGroupId): ChartId => {
  const existingId = defaultStatisticsChartIds.get(boundaryTerritoryGroupId);
  if (existingId) {
    return existingId;
  }
  const newId = createChartId();
  defaultStatisticsChartIds.set(boundaryTerritoryGroupId, newId);
  return newId;
};

const getBTGIdFromDefaultStatsChart = (chartId: ChartId): BoundaryTerritoryGroupId | undefined => {
  for (const [btgId, assignedChartId] of defaultStatisticsChartIds.entries()) {
    if (assignedChartId === chartId) {
      return btgId;
    }
  }
  return undefined;
};

export const useDefaultBTGStats = () => {
  const [t] = useTranslation();
  const boundaryGroups = useBoundaryGroupsSelector();
  const boundaryTerritoryGroups = useBoundaryTerritoryGroupsSelector();
  const { getBoundaryTerritoryGroupName } = useBoundaryTerritoryGroupDisplayName();

  const boundaryGroupsLookup = useMemo(() => (
    boundaryGroups.reduce((acc, group) => {
      acc.set(group.id, group);
      return acc;
    }, new Map<BoundaryGroupId, typeof boundaryGroups[number]>())
  ), [boundaryGroups]);

  const getIsChartRestrictedForBoundaryGroup = useCallback((boundaryGroupId: BoundaryGroupId): boolean => {
    const boundaryGroup = boundaryGroupsLookup.get(boundaryGroupId);
    if (!boundaryGroup) {
      return true;
    }
    const remainingWmsCalls = boundaryGroup.wms.geometryRemainingCalls ?? 1;
    const remainingMotCalls = boundaryGroup.mot.geometryRemainingCalls ?? 1;

    return remainingWmsCalls < 1 || remainingMotCalls < 1;
  }, [boundaryGroupsLookup]);

  const defaultTerritoryStatsCharts: ChartBoundaryStatistics[] = useMemo(() => (
    boundaryTerritoryGroups
      .filter((btg, index, self) => (
        // default statistics make sense only once per boundary group
        // so if multiple BTGs have the same BG we filter them out
        index === self.findIndex((t) => t.boundaryGroupId === btg.boundaryGroupId)
      )).map((btg) => {
        if (!btg.settings.defaultStatistics
          || btg.settings.defaultStatistics.length === 0
          || getIsChartRestrictedForBoundaryGroup(btg.boundaryGroupId)
        ) {
          return null;
        }

        const defaultStatisticChartId = getOrCreateBoundaryDefaultStatsChartId(btg.boundaryTerritoryGroupId);

        return createChartBoundaryStatistics({
          id: defaultStatisticChartId,
          boundaryGroupId: btg.boundaryGroupId,
          title: getBoundaryTerritoryGroupName(btg) || t('Loading...'),
          shouldNormalize: btg.settings.defaultStatistics.length > 1,
          metrics: btg.settings.defaultStatistics.map((statistic) => {
            if (statistic.type === ChartMetricType.LocationCount) {
              const column = statistic.metric
                ? { spreadsheetId: statistic.virtualSpreadsheetId, columnId: statistic.metric }
                : undefined;
              return createChartMetricLocationCount(statistic.virtualSpreadsheetId, column, statistic.columnValue);
            }
            if (statistic.type === ChartMetricType.SpreadsheetColumn) {
              return createChartMetricSpreadsheetColumn({
                spreadsheetId: statistic.virtualSpreadsheetId,
                columnId: statistic.metric,
              });
            }
            if (statistic.type === ChartMetricType.Demographic) {
              return createChartMetricDemographic('' + statistic.metric);
            }
            return null;
          }).filter(notNull),
        });
      }).filter(notNull)
  ), [boundaryTerritoryGroups, getBoundaryTerritoryGroupName, getIsChartRestrictedForBoundaryGroup, t]);

  const getBoundaryDefaultStatsChartForBoundaryGroup = useCallback((
    boundaryTerritoryGroupId: BoundaryTerritoryGroupId,
  ) => {
    return defaultTerritoryStatsCharts.find((chart => chart.boundaryGroupId === boundaryTerritoryGroupId));
  }, [defaultTerritoryStatsCharts]);

  return {
    defaultTerritoryStatsCharts,
    getBTGIdFromDefaultStatsChart,
    getBoundaryDefaultStatsChartForBoundaryGroup,
  };
};
